/**
 * InvoiceTemplateEditor — full editor for every label / copy line on
 * the invoice PDF (`utils/invoice_generator.py`).
 *
 * Backed by `GET/PUT /api/config/invoice-template`. Includes a live
 * preview via the existing `/api/companies/{id}/invoice-preview`
 * endpoint so the admin can iterate on wording with instant feedback.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Save, RefreshCw, Loader2, CheckCircle2, Eye, Plus, Trash2, ArrowUp, ArrowDown, Type, AlignLeft, Columns, Minus, Move, Table as TableIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  {
    title: 'Header banner',
    fields: [
      { k: 'title', label: 'Invoice title', hint: 'Top-right of the teal banner. Default: INVOICE' },
      { k: 'tagline', label: 'Tagline (under issuer name)', hint: 'Shown only when no logo is uploaded' },
      { k: 'header_subtitle', label: 'Header subtitle (under "INVOICE")' },
    ],
  },
  {
    title: 'Section labels',
    fields: [
      { k: 'from_label',    label: 'Issuer block label',   hint: 'Default: FROM' },
      { k: 'bill_to_label', label: 'Customer block label', hint: 'Default: BILL TO' },
    ],
  },
  {
    title: 'Invoice meta box (top-right)',
    fields: [
      { k: 'invoice_no_label', label: 'Invoice number label' },
      { k: 'issue_date_label', label: 'Issue date label' },
      { k: 'status_label',     label: 'Status label' },
      { k: 'status_paid',      label: 'Status value when paid', hint: 'Used to detect "paid" → green colour' },
    ],
  },
  {
    title: 'Items table',
    fields: [
      { k: 'col_desc',   label: 'Column 1 header (description)' },
      { k: 'col_qty',    label: 'Column 2 header (quantity)' },
      { k: 'col_amount', label: 'Column 3 header (amount)' },
      { k: 'line_item_text',  label: 'Single-row description', hint: 'Same on every invoice for legal/accounting reasons' },
      { k: 'bonus_line_text', label: 'Bonus credit line',      hint: 'Use {bonus} for the amount placeholder' },
    ],
  },
  {
    title: 'Totals & payment',
    fields: [
      { k: 'subtotal_label',         label: 'Subtotal label' },
      { k: 'total_due_label',        label: 'Total due label' },
      { k: 'payment_details_label',  label: 'Payment details block heading' },
    ],
  },
  {
    title: 'Footer',
    fields: [
      { k: 'footer_thank_you',     label: 'Thank-you line', hint: 'Use {company} to insert the issuing company name' },
      { k: 'footer_signature_note', label: 'Signature note' },
      { k: 'page_label',     label: 'Page label (right side)' },
      { k: 'reference_label', label: 'Reference label (right side)' },
    ],
  },
];

export default function InvoiceTemplateEditor({ token }) {
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [err, setErr] = useState('');
  const [companies, setCompanies] = useState([]);
  const [previewCompanyId, setPreviewCompanyId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const r = await fetch(`${API_URL}/api/config/invoice-template`, { headers });
      if (r.ok) setTpl(await r.json());
      else if (r.status === 401) {
        // Stale admin token — make the empty state explicit instead of
        // pretending defaults are persisted.
        setTpl({});
        throw new Error('Session expired — please re-login.');
      } else throw new Error(`HTTP ${r.status}`);
    } catch (e) { setErr(e.message || 'Failed to load'); }
    try {
      const r2 = await fetch(`${API_URL}/api/companies/`, { headers });
      if (r2.ok) {
        const list = await r2.json();
        setCompanies(Array.isArray(list) ? list : []);
        if ((list || []).length && !previewCompanyId) setPreviewCompanyId(list[0].id);
      }
    } catch (_) { /* preview is best-effort */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setTpl(prev => ({ ...(prev || {}), [k]: v }));

  const save = async () => {
    if (!tpl) return;
    setSaving(true); setSaved(''); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/config/invoice-template`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(tpl),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setTpl(d);
      setSaved('Saved'); setTimeout(() => setSaved(''), 3000);
    } catch (e) { setErr(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const refreshPreview = useCallback(async () => {
    if (!previewCompanyId) return;
    setLoadingPreview(true);
    try {
      const r = await fetch(`${API_URL}/api/companies/${previewCompanyId}/invoice-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (_) { /* keep previous on transient failure */ }
    finally { setLoadingPreview(false); }
  }, [previewCompanyId, token, previewUrl]);

  if (loading || !tpl) {
    return <div className="p-8 text-center text-slate-500" data-testid="invoice-tpl-loading">Loading invoice template…</div>;
  }

  return (
    <div className="space-y-4" data-testid="invoice-template-editor">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl px-4 py-3">
        <div className="flex items-center gap-2.5">
          <FileText className="w-5 h-5 text-cyan-700" />
          <div>
            <h2 className="text-base font-bold text-slate-900">Invoice template</h2>
            <p className="text-xs text-slate-600">Edit every label and static line that appears on the generated PDF. Per-company language overrides are managed on each company record.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saved && <span className="text-emerald-600 text-sm font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />{saved}</span>}
          <button onClick={save} disabled={saving} data-testid="invoice-tpl-save"
            className="px-4 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-md disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save template
          </button>
        </div>
      </div>

      {err && (
        <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs">{err}</div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        {/* Edit form */}
        <div className="xl:col-span-3 space-y-3">
          {SECTIONS.map(section => (
            <div key={section.title} className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="text-[11px] uppercase font-bold text-slate-600 tracking-wider mb-3">{section.title}</h3>
              <div className="space-y-3">
                {section.fields.map(f => (
                  <div key={f.k}>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">{f.label}</label>
                    <input
                      type="text"
                      value={tpl[f.k] ?? ''}
                      onChange={(e) => setField(f.k, e.target.value)}
                      data-testid={`invoice-tpl-${f.k}`}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                    {f.hint && <p className="text-[11px] text-slate-500 mt-0.5">{f.hint}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Free-form custom blocks editor — admin can add arbitrary
              text / two-column rows / tables / dividers BETWEEN the
              BILL TO block and the items table on every invoice. */}
          <CustomBlocksEditor
            blocks={tpl.custom_blocks || []}
            onChange={(next) => setTpl(prev => ({ ...(prev || {}), custom_blocks: next }))}
          />
        </div>

        {/* Preview pane */}
        <div className="xl:col-span-2">
          <div className="sticky top-4 bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[11px] uppercase font-bold text-slate-600 tracking-wider flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5" /> Live preview
              </h3>
              <button onClick={refreshPreview} disabled={loadingPreview || !previewCompanyId}
                data-testid="invoice-tpl-refresh"
                className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 disabled:opacity-50 flex items-center gap-1">
                {loadingPreview ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Refresh
              </button>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1">Preview as company</label>
              <select
                value={previewCompanyId || ''}
                onChange={(e) => setPreviewCompanyId(e.target.value)}
                data-testid="invoice-tpl-preview-company"
                className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs"
              >
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <p className="text-[11px] text-amber-700">
              Save the template first, then click <b>Refresh</b> — the preview reads the SAVED state.
            </p>
            {previewUrl ? (
              <object data={`${previewUrl}#zoom=70&navpanes=0`} type="application/pdf"
                className="w-full h-[600px] rounded-lg border border-slate-200 bg-slate-50">
                <p className="p-3 text-sm text-slate-600">PDF preview not supported in this browser.</p>
              </object>
            ) : (
              <div className="w-full h-[600px] rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-slate-500 text-sm text-center px-4">
                Pick a company above and click <b>&nbsp;Refresh&nbsp;</b> to generate a sample invoice.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── CustomBlocksEditor ──────────────── */

const BLOCK_PRESETS = [
  { type: 'heading',     icon: Type,       label: 'Heading',     factory: () => ({ type: 'heading',     text: 'Section heading', color: 'accent' }) },
  { type: 'paragraph',   icon: AlignLeft,  label: 'Paragraph',   factory: () => ({ type: 'paragraph',   text: 'Free text paragraph. Supports {company}, {customer}, {invoice_no}, {date}, {amount}, {email}, {vat} placeholders.' }) },
  { type: 'two_columns', icon: Columns,    label: 'Two columns', factory: () => ({ type: 'two_columns', left: 'Left column', right: 'Right column' }) },
  { type: 'table',       icon: TableIcon,  label: 'Table',       factory: () => ({ type: 'table',       headers: ['Item', 'Detail'], rows: [['Row 1 — col A', 'Row 1 — col B']] }) },
  { type: 'divider',     icon: Minus,      label: 'Divider',     factory: () => ({ type: 'divider' }) },
  { type: 'spacer',      icon: Move,       label: 'Spacer',      factory: () => ({ type: 'spacer', mm: 4 }) },
];

function CustomBlocksEditor({ blocks, onChange }) {
  const set = (idx, patch) => onChange(blocks.map((b, i) => i === idx ? { ...b, ...patch } : b));
  const add = (preset) => onChange([...(blocks || []), preset.factory()]);
  const remove = (idx) => onChange(blocks.filter((_, i) => i !== idx));
  const move = (idx, delta) => {
    const next = [...blocks];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="bg-white border-2 border-cyan-200 rounded-xl p-4 space-y-3" data-testid="custom-blocks-editor">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Plus className="w-4 h-4 text-cyan-700" />Custom content blocks</h3>
          <p className="text-[11px] text-slate-500">Free-form text, columns and tables rendered between BILL TO and the items table on every invoice.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {BLOCK_PRESETS.map(p => (
          <button key={p.type} type="button" onClick={() => add(p)}
            data-testid={`block-add-${p.type}`}
            className="px-2.5 py-1 rounded-md text-xs font-semibold bg-cyan-50 border border-cyan-200 text-cyan-800 hover:bg-cyan-100 flex items-center gap-1.5">
            <p.icon className="w-3 h-3" /> {p.label}
          </button>
        ))}
      </div>

      {(blocks || []).length === 0 && (
        <div className="text-center text-xs text-slate-500 py-6 border-2 border-dashed border-slate-200 rounded-lg">
          No custom blocks yet. Use the buttons above to add your first one.
        </div>
      )}

      <div className="space-y-2">
        {(blocks || []).map((b, idx) => (
          <BlockRow key={idx} idx={idx} block={b} total={blocks.length}
                    onChange={(patch) => set(idx, patch)}
                    onRemove={() => remove(idx)}
                    onMove={(delta) => move(idx, delta)} />
        ))}
      </div>
    </div>
  );
}

function BlockRow({ idx, block, total, onChange, onRemove, onMove }) {
  const type = block.type;
  const Icon = BLOCK_PRESETS.find(p => p.type === type)?.icon || Type;
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/40" data-testid={`block-${idx}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-600">
          <Icon className="w-3.5 h-3.5 text-cyan-700" /> {type}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={idx === 0} data-testid={`block-${idx}-up`}
            className="p-1 rounded text-slate-500 hover:bg-slate-200 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
          <button onClick={() => onMove(+1)} disabled={idx === total - 1} data-testid={`block-${idx}-down`}
            className="p-1 rounded text-slate-500 hover:bg-slate-200 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
          <button onClick={onRemove} data-testid={`block-${idx}-remove`}
            className="p-1 rounded text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {type === 'heading' && (
        <div className="space-y-2">
          <input type="text" value={block.text || ''} onChange={(e) => onChange({ text: e.target.value })}
            data-testid={`block-${idx}-text`}
            className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm font-bold" />
          <select value={block.color || 'accent'} onChange={(e) => onChange({ color: e.target.value })}
            className="px-2 py-1 border border-slate-300 rounded text-xs">
            <option value="accent">Accent (teal)</option>
            <option value="ink">Ink (dark)</option>
            <option value="muted">Muted (grey)</option>
          </select>
        </div>
      )}

      {type === 'paragraph' && (
        <textarea rows={3} value={block.text || ''} onChange={(e) => onChange({ text: e.target.value })}
          data-testid={`block-${idx}-text`}
          className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm" />
      )}

      {type === 'two_columns' && (
        <div className="grid grid-cols-2 gap-2">
          <textarea rows={2} value={block.left || ''} onChange={(e) => onChange({ left: e.target.value })}
            placeholder="Left column" data-testid={`block-${idx}-left`}
            className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
          <textarea rows={2} value={block.right || ''} onChange={(e) => onChange({ right: e.target.value })}
            placeholder="Right column" data-testid={`block-${idx}-right`}
            className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
        </div>
      )}

      {type === 'divider' && <p className="text-[11px] text-slate-500 italic">A thin horizontal rule.</p>}

      {type === 'spacer' && (
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-600">Gap (mm)</label>
          <input type="number" min={1} max={40} value={block.mm ?? 4}
            onChange={(e) => onChange({ mm: Number(e.target.value) })}
            data-testid={`block-${idx}-mm`}
            className="w-20 px-2 py-1 border border-slate-300 rounded text-xs" />
        </div>
      )}

      {type === 'table' && <TableBlockEditor block={block} idx={idx} onChange={onChange} />}
    </div>
  );
}

function TableBlockEditor({ block, idx, onChange }) {
  const headers = block.headers || [];
  const rows = block.rows || [];
  const setHeader = (i, v) => onChange({ headers: headers.map((h, k) => k === i ? v : h) });
  const setCell = (r, c, v) => onChange({ rows: rows.map((row, ri) => ri === r ? row.map((cell, ci) => ci === c ? v : cell) : row) });
  const addCol = () => onChange({ headers: [...headers, `Col ${headers.length + 1}`], rows: rows.map(r => [...r, '']) });
  const delCol = (i) => onChange({ headers: headers.filter((_, k) => k !== i), rows: rows.map(r => r.filter((_, k) => k !== i)) });
  const addRow = () => onChange({ rows: [...rows, headers.map(() => '')] });
  const delRow = (i) => onChange({ rows: rows.filter((_, k) => k !== i) });

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        <button type="button" onClick={addCol} data-testid={`block-${idx}-add-col`}
          className="px-2 py-0.5 rounded text-[11px] font-semibold bg-white border border-slate-300 hover:bg-slate-50">+ Column</button>
        <button type="button" onClick={addRow} data-testid={`block-${idx}-add-row`}
          className="px-2 py-0.5 rounded text-[11px] font-semibold bg-white border border-slate-300 hover:bg-slate-50">+ Row</button>
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs w-full border border-slate-200">
          <thead className="bg-slate-100">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className="p-1 border border-slate-200">
                  <div className="flex items-center gap-1">
                    <input value={h} onChange={(e) => setHeader(i, e.target.value)}
                      data-testid={`block-${idx}-header-${i}`}
                      className="w-full px-1.5 py-0.5 border border-slate-300 rounded text-xs font-bold" />
                    <button onClick={() => delCol(i)} className="text-rose-600 hover:bg-rose-50 p-0.5 rounded"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} className="p-1 border border-slate-200">
                    <input value={cell} onChange={(e) => setCell(ri, ci, e.target.value)}
                      data-testid={`block-${idx}-row-${ri}-${ci}`}
                      className="w-full px-1.5 py-0.5 border border-slate-300 rounded text-xs" />
                  </td>
                ))}
                <td className="p-1 border border-slate-200 w-6">
                  <button onClick={() => delRow(ri)} className="text-rose-600 hover:bg-rose-50 p-0.5 rounded"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
